import React, { useCallback, useLayoutEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '@api/client';
import { useOrganization } from '@components/OrganizationBoundary';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import { useAuth } from '@hooks/useAuth';
import { useIsDemoOrganization } from '@hooks/useIsDemoOrganization';
import { ANNOUNCEMENT_MEDIA_VIEWER_TOUR_METADATA_KEY } from '@components/Modals/AnnouncementModal/AnnouncementModalContainer';
import { useResponsive } from '@hooks/useResponsive';
import { useAssetMVPermissions } from '@hooks';
import { CrossIcon } from '@icons';
import './MediaViewerTourBanner.less';

export default function MediaViewerTourBanner() {
  const { isLgWidth } = useResponsive();
  const { user, updateUserProfile } = useAuth(false);
  const isDemoOrganization = useIsDemoOrganization();
  const { currentOrganization } = useOrganization(false);
  const { tourData } = useOnboardingProcess();
  const canLeaveComment = useAssetMVPermissions('comment');
  const [_, setSearchParams] = useSearchParams();
  const [isDismissed, setIsDismissed] = useState(() => {
    if (!user || user.type !== 'internal') {
      return (
        localStorage.getItem(ANNOUNCEMENT_MEDIA_VIEWER_TOUR_METADATA_KEY) ===
        'true'
      );
    }
    return !!user.metadata[ANNOUNCEMENT_MEDIA_VIEWER_TOUR_METADATA_KEY];
  });

  const hasActiveTour = !!tourData?.type;
  const bannerHeight = isLgWidth ? '28px' : '36px';

  const isVisible =
    canLeaveComment &&
    !isDemoOrganization &&
    !isDismissed &&
    !hasActiveTour &&
    !(
      user &&
      user.type === 'internal' &&
      currentOrganization?.entity?.features.disableAnnouncements
    );

  useLayoutEffect(() => {
    if (!isVisible) return;
    document.documentElement.style.setProperty(
      '--demo-mode-panel-width',
      bannerHeight
    );
    return () => {
      document.documentElement.style.removeProperty('--demo-mode-panel-width');
    };
  }, [isVisible]);

  const onDismiss = useCallback(async () => {
    setIsDismissed(true);
    if (!user || user.type !== 'internal') {
      localStorage.setItem(ANNOUNCEMENT_MEDIA_VIEWER_TOUR_METADATA_KEY, 'true');
      return;
    }
    const metadata = {
      ...(user?.metadata || {}),
      [ANNOUNCEMENT_MEDIA_VIEWER_TOUR_METADATA_KEY]: true
    };
    updateUserProfile({
      metadata
    });
    await apiClient.profile.profileControllerUpdateProfileMetadata({
      metadata
    });
  }, [updateUserProfile, user]);

  const onStartTour = useCallback(() => {
    onDismiss();
    setSearchParams(
      (prev) => {
        prev.set(
          'startTour',
          isLgWidth
            ? 'announcement-media-viewer'
            : 'announcement-media-viewer-mobile'
        );
        return prev;
      },
      { replace: true }
    );
  }, [onDismiss, setSearchParams]);

  if (!isVisible) return null;

  return (
    <div className="media-viewer-tour-banner" style={{ height: bannerHeight }}>
      <strong>Media Viewer 2.0.</strong>
      {isLgWidth && <>See what&apos;s new in the updated media viewer</>}
      <button
        type="button"
        className="media-viewer-tour-banner_btn"
        onClick={onStartTour}
      >
        Start a quick tour
      </button>
      <button
        type="button"
        className="media-viewer-tour-banner_close-btn"
        onClick={onDismiss}
        aria-label="Dismiss announcement"
      >
        <CrossIcon />
      </button>
    </div>
  );
}
